import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "./request";
import { useGetUserCompanies } from "./query";
import { FULL_USER_COMPANIES_PAYLOAD, useCreateCategory } from "./common/noticeManagementUtils";

const BASE_URL = "/api/v1/litigation_management";

// Re-export shared utilities for backward compatibility
export { FULL_USER_COMPANIES_PAYLOAD, useCreateCategory };

// Get all litigations (with auto-filtering by user's companies)
export function useGetLitigations(payload: any, enabled = true) {
  const { userCompanies, isFetching: isLoadingCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const companyIds = (userCompanies || []).map((uc: any) => uc.id);

  const associateCompanyIds = new Set<string>();
  const locationIds = new Set<string>();
  (userCompanies || []).forEach((company: any) => {
    (company.associate_companies || []).forEach((assocCompany: any) => {
      if (assocCompany.id) {
        associateCompanyIds.add(assocCompany.id);
      }
      (assocCompany.locations || []).forEach((location: any) => {
        if (location.id) {
          locationIds.add(location.id);
        }
      });
    });
  });

  const hasCompanies = companyIds && companyIds.length > 0;
  const payloadFilters = (payload && payload.filters) || {};

  const enhancedPayload = hasCompanies ? {
    ...(payload || {}),
    filters: {
      ...payloadFilters,
      ...(payloadFilters.company_id_eq ? {} : { company_id_in: companyIds }),
    }
  } : (payload || {});

  const { data, isFetching, refetch } = useQuery(
    ["litigations", enhancedPayload],
    async () => await api.get(`${BASE_URL}/litigations`, enhancedPayload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && hasCompanies,
    }
  );

  const items = !hasCompanies ? [] : ((data && data.data && data.data.items) ? data.data.items : []);
  const meta = (data && data.data && data.data.meta) ? data.data.meta : {};
  const counts = (meta && meta.counts) ? meta.counts : {};

  return {
    litigations: items,
    total: (meta && meta.total_count) || 0,
    counts,
    isFetching: isFetching || isLoadingCompanies,
    refetch,
    companyIds,
  };
}

// Get single litigation
export function useGetLitigation(id: string, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ["litigation", id],
    async () => await api.get(`${BASE_URL}/litigations/${id}`),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!id && enabled,
    }
  );

  return {
    litigation: (data || {}).data || {},
    isFetching,
    refetch,
  };
}

// Create litigation
export function useCreateLitigation(onSuccess?: any, onError?: any) {
  const queryClient = useQueryClient();
  const {
    mutate: createLitigation,
    error,
    isLoading: creating,
  } = useMutation(
    ["createLitigation"],
    async (payload) => await api.post(`${BASE_URL}/litigations`, payload),
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
            ? response.data
            : (response && response.response && response.response.data);
        queryClient.invalidateQueries(["litigations"]);
        onSuccess(data);
      },
    }
  );

  return { createLitigation, error, creating };
}

// Update litigation
export function useUpdateLitigation(onSuccess?: any, onError?: any) {
  const {
    mutate: updateLitigation,
    error,
    isLoading: updating,
  } = useMutation(
    ["updateLitigation"],
    async (payload: any) => {
      const { id, ...updateData } = payload;
      return await api.put(`${BASE_URL}/litigations/${id}`, updateData);
    },
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
            ? response.data
            : (response && response.response && response.response.data);
        onSuccess(data);
      },
    }
  );

  return { updateLitigation, error, updating };
}

// Change litigation status
export function useChangeLitigationStatus(onSuccess?: any, onError?: any) {
  const {
    mutate: changeStatus,
    error,
    isLoading: updating,
  } = useMutation(
    ["changeLitigationStatus"],
    async (payload: any) => {
      const { id, ...statusData } = payload;
      return await api.post(`${BASE_URL}/litigations/${id}/change_status`, statusData);
    },
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
            ? response.data
            : (response && response.response && response.response.data);
        onSuccess(data);
      },
    }
  );

  return { changeStatus, error, updating };
}

// Get litigation history
export function useGetLitigationHistory(
  litigationId: string | undefined,
  fields?: string,
  enabled = true
) {
  const payload = fields ? { fields: fields } : {};

  const { data, isFetching, refetch } = useQuery(
    ["litigationHistory", litigationId],
    async () =>
      await api.get(
        `${BASE_URL}/litigations/${litigationId}/history`,
        payload
      ),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && !!litigationId,
    }
  );

  const historyItems = data && data.data ? data.data : [];

  return {
    historyItems,
    isFetching,
    refetch,
  };
}

// Get litigation documents
export function useGetLitigationDocuments(
  litigationId: string,
  enabled = true
) {
  const payload = {
    include: ["asset"],
    includes: ["asset"],
    filters: {
      litigation_id_eq: litigationId,
    },
  };

  const { data, isFetching, refetch } = useQuery(
    ["litigation_documents", litigationId],
    async () => await api.get(`${BASE_URL}/litigation_documents`, payload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && !!litigationId,
    }
  );

  const items = (data && data.data && data.data.items) ? data.data.items : [];
  const meta = (data && data.data && data.data.meta) ? data.data.meta : {};

  return {
    litigationDocuments: items,
    total: (meta && meta.total_count) || 0,
    isFetching,
    refetch,
  };
}

// Get categories for litigations
export function useGetLitigationCategories(
  companyId: string,
  enabled = true
) {
  const payload = {
    scopes: ["litigations"],
    sorts: ["created_at desc"],
  };

  const { data, isFetching, refetch } = useQuery(
    ["litigation_categories", companyId],
    async () => await api.get(`/api/v1/companies/${companyId}/categories`, payload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && !!companyId,
    }
  );

  const items = (data && data.data && data.data.items) ? data.data.items : [];

  return {
    categories: items,
    isFetching,
    refetch,
  };
}

